<template>
  <section class="h-full grid grid-rows-[auto_1fr] bg-white border-t">
    <div class="flex justify-end items-center px-4 py-2 gap-2">
      <BaseControlsButtonIcon
        v-if="mode === 'edit' && editedLine"
        :tooltip-text="$t('common.tooltip.filterMaterialsByGroup')"
        @click="openFiltersModal">
        <IconFilters class="size-4" />
      </BaseControlsButtonIcon>

      <button
        v-if="mode === 'edit'"
        type="button"
        @click="addNewLine"
        class="px-2 py-1 font-normal rounded-md border border-gray-300">
        <span class="flex gap-2 justify-center items-center text-sm capitalize text-theme-500">
          <IconAddSmall />
          <p>{{ $t('common.actions.addNew') }}</p>
        </span>
      </button>
    </div>

    <!-- Filter Tags Display -->
    <div
      v-if="activeFilters && activeFilters.length > 0"
      class="flex flex-wrap gap-1.5 my-2 justify-center px-4">
      <div
        v-for="(item, index) in visibleTags"
        :key="index"
        class="flex items-center p-1 m-1 text-xs font-medium text-center text-gray-500 align-middle transition-all bg-gray-200 border rounded-md shadow-sm w-fit">
        {{ item.name }}

        <IconXmark
          class="w-3.5 h-3.5 ml-auto cursor-pointer"
          @click="handleSelection(item)"></IconXmark>
      </div>
      <button
        v-if="activeFilters && activeFilters.length > maxVisibleTags"
        type="button"
        class="mx-1 text-xs hover:text-theme-700"
        @click="toggleVisibleTags">
        {{
          showMore
            ? $t('common.actions.showLess')
            : `${$t('common.actions.showMore')} (${remainingTags})`
        }}
      </button>
    </div>

    <div v-if="editedLine" class="grid w-full h-full grid-rows-[auto_1fr] gap-2 overflow-hidden">
      <div class="grid grid-cols-[1fr_2fr_auto] w-full h-full gap-10 overflow-auto px-6">
        <!-- Image -->
        <div
          class="flex"
          :class="
            isHorizontalLayout
              ? 'justify-self-end flex-col items-start justify-start'
              : 'row-start-1 col-span-2 items-center gap-8'
          ">
          <div class="flex justify-center items-center mb-2 size-36">
            <img
              v-if="editedLine.material.image"
              :src="`data:image/jpeg;base64,${editedLine.material.image}`"
              alt="placeholder"
              class="object-cover w-full h-auto rounded-md" />
            <img
              v-else
              src="@/assets/images/placeholder-image.webp"
              alt="placeholder"
              class="rounded-md object-fit" />
          </div>
          <div class="flex flex-col">
            <p class="text-sm text-gray-500">{{ editedLine.materialCode }}</p>
            <p class="text-lg font-medium">{{ editedLine.materialName }}</p>
          </div>
        </div>
        <!-- Form -->
        <div
          class="grid grid-rows-[1fr_auto] gap-4 justify-self-start"
          :class="isHorizontalLayout ? '' : 'row-start-2 col-span-2'">
          <div class="flex flex-wrap gap-4 justify-start items-start max-w-2xl">
            <div class="w-full">
              <label class="text-sm font-medium">{{ $t('material.material') }}</label>
              <BaseTableCellSearchbox
                :error="validate('materialCode', editedLine.id)"
                v-model="searchMaterialQuery"
                full-mode
                :mode="mode"
                :placeholderText="
                  editedLine.materialCode
                    ? `${editedLine.materialCode} - ${editedLine.materialName} `
                    : $t('salesDocuments.search')
                "
                @update-search="onUpdateMaterialAlterCodesSearch(searchMaterialQuery, editedLine)"
                @clear-selection="clearItem(editedLine)" />
            </div>

            <BaseSelectDropdown
              name="warehouseDropdown"
              size="sm"
              flat
              :mode="mode"
              :label="$t('salesDocuments.headers.warehouse')"
              :items="dropdownWarehouses"
              :selected-item="{
                value: editedLine.warehouseId ?? '',
                name: editedLine.warehouse?.name ?? '',
              }"
              show-error-as-tooltip
              :error="validate('warehouse.name', editedLine.id)"
              @update:selected="handleWarehouseSelection($event, editedLine)"
              class="w-52" />

            <!-- Dynamic Quantity Fields -->
            <template v-if="editedLine">
              <div
                v-for="quantityField in getDynamicQuantityFields(
                  editedLine,
                  modifiedSalesDocument?.documentHeader?.documentType,
                  measurementUnits
                )"
                :key="`qty-${editedLine.id}-${quantityField.unitIndex}`"
                class="max-w-52">
                <label class="text-sm font-medium">
                  {{
                    quantityField.unitIndex === 1
                      ? $t('salesDocuments.quantity')
                      : $t('salesDocuments.quantity') + ` ${quantityField.unitIndex}`
                  }}
                  {{ quantityField.label ? `(${quantityField.label})` : '' }}
                </label>
                <BaseInput
                  type="number"
                  color="base"
                  icon="right"
                  class="h-8 bg-white"
                  :model-value="getQuantityValue(editedLine!, quantityField.unitIndex)"
                  show-error-as-tooltip
                  :disabled="isQuantityInputDisabled(editedLine!, quantityField.unitIndex)"
                  :error="validate('totalQuantity', editedLine.id)"
                  @update:model-value="
                    (value) => {
                      setQuantityValue(editedLine!, quantityField.unitIndex, Number(value) || 0);
                      inputUpdated('quantity', editedLine!);
                    }
                  ">
                </BaseInput>
              </div>
            </template>

            <div
              v-if="
                editedLine &&
                (modifiedSalesDocument?.documentHeader?.documentType?.totalMeasurementUnits || 1) >
                  1
              "
              class="max-w-52">
              <label class="text-sm font-medium">{{ $t('salesDocuments.totalQuantity') }}</label>
              <div
                class="flex items-center justify-end px-3 h-8 bg-gray-50 rounded-md text-sm font-medium text-gray-700 border">
                {{ editedLine?.totalQuantity }}
              </div>
            </div>

            <div class="max-w-52">
              <label class="text-sm font-medium">{{ $t('salesDocuments.unitPrice') }}</label>
              <BaseInput
                type="number"
                color="base"
                icon="right"
                price
                class="h-8 bg-white"
                v-model="editedLine.unitPrice"
                show-error-as-tooltip
                :disabled="isUnitPriceDisabled"
                :error="validate('unitPrice', editedLine.id)"
                @update:model-value="inputUpdated('unitPrice', editedLine)">
                <template #icon-right>
                  <div
                    class="flex items-center uppercase font-light px-1 h-[30px] text-gray-400 bg-slate-200 rounded-r-md border-l border-gray-300 text-xs">
                    {{ '€' }}
                  </div>
                </template>
              </BaseInput>
            </div>

            <div class="max-w-52">
              <label class="text-sm font-medium">{{
                $t('salesDocuments.unitDiscountPercentage')
              }}</label>
              <BaseInput
                type="number"
                color="base"
                icon="right"
                price
                class="h-8 bg-white"
                :readonly="!editedLine.allowDiscount"
                :disabled="mode === 'view' || !editedLine.allowDiscount"
                v-model="editedLine.percentageDiscount"
                show-error-as-tooltip
                @update:model-value="inputUpdated('percentageDiscount', editedLine)">
              </BaseInput>
            </div>

            <div class="max-w-52">
              <label class="text-sm font-medium">{{ $t('salesDocuments.discountValue') }}</label>
              <BaseInput
                type="number"
                color="base"
                icon="right"
                price
                :readonly="!editedLine.allowDiscount"
                :disabled="mode === 'view' || !editedLine.allowDiscount"
                class="h-8 bg-white"
                v-model="editedLine.unitDiscount"
                show-error-as-tooltip
                @update:model-value="inputUpdated('unitDiscount', editedLine)">
                <template #icon-right>
                  <div
                    class="flex items-center uppercase font-light px-1 h-[30px] text-gray-400 bg-slate-200 rounded-r-md border-l border-gray-300 text-xs">
                    {{ '€' }}
                  </div>
                </template>
              </BaseInput>
            </div>

            <div class="max-w-52">
              <label class="text-sm font-medium">{{ $t('salesDocuments.netTotal') }}</label>
              <BaseInput
                type="number"
                color="base"
                icon="right"
                price
                class="h-8 bg-white"
                :disabled="mode === 'view'"
                v-model="editedLine.netTotal"
                @update:model-value="inputUpdated('netTotal', editedLine)"
                show-error-as-tooltip>
                <template #icon-right>
                  <div
                    class="flex items-center uppercase font-light px-1 h-[30px] text-gray-400 bg-slate-200 rounded-r-md border-l border-gray-300 text-xs">
                    {{ '€' }}
                  </div>
                </template>
              </BaseInput>
            </div>

            <div class="max-w-52">
              <label class="text-sm font-medium">{{ $t('salesDocuments.vat') }}</label>
              <BaseInput
                type="number"
                color="base"
                icon="right"
                class="h-8 bg-white"
                readonly
                disabled
                price
                v-model="editedLine.lineVatTotal"
                show-error-as-tooltip>
              </BaseInput>
            </div>

            <div class="max-w-52">
              <label class="text-sm font-medium">{{ $t('salesDocuments.grossTotal') }}</label>
              <BaseInput
                type="number"
                color="base"
                icon="right"
                price
                class="h-8 bg-white"
                v-model="editedLine.grossTotal"
                :disabled="mode === 'view'"
                show-error-as-tooltip
                @update:model-value="inputUpdated('grossTotal', editedLine)">
                <template #icon-right>
                  <div
                    class="flex items-center uppercase font-light px-1 h-[30px] text-gray-400 bg-slate-200 rounded-r-md border-l border-gray-300 text-xs">
                    {{ '€' }}
                  </div>
                </template>
              </BaseInput>
            </div>
            <div class="max-w-52">
              <label class="text-sm font-medium">{{ $t('salesDocuments.exists') }}</label>
              <BaseToggle
                :disable="mode === 'view'"
                class="flex justify-center items-center h-8 text-sm"
                :name="`existsToggle-${editedLine.id}`"
                v-model="editedLine.exists"
                @change="inputUpdated('exists', editedLine)" />
            </div>

            <div class="max-w-52">
              <label class="text-sm font-medium">{{ $t('salesDocuments.notExists') }}</label>
              <BaseToggle
                :disable="mode === 'view'"
                class="flex justify-center items-center h-8 text-sm"
                :name="`notExistsToggle-${editedLine.id}`"
                v-model="editedLine.notExists"
                @change="inputUpdated('notExists', editedLine)" />
            </div>
          </div>

          <!-- Select Buttons -->
          <div class="flex gap-6 justify-start items-center mt-2">
            <button
              type="button"
              :disabled="previousButtonDisabled"
              @click="selectPreviousLine"
              class="px-4 py-2 font-light rounded-md border"
              :class="
                previousButtonDisabled
                  ? ' text-gray-300 border-gray-200'
                  : 'text-gray-500 border-gray-300'
              ">
              <span class="flex gap-2 justify-center items-center text-sm capitalize">
                <IconChevronLeft class="size-4" />
                <p>{{ $t('common.previous') }}</p>
              </span>
            </button>
            <button
              type="button"
              :disabled="nextButtonDisabled"
              @click="selectNextLine"
              class="px-4 py-2 font-light rounded-md border"
              :class="
                nextButtonDisabled
                  ? 'text-gray-300 border-gray-200'
                  : 'text-gray-500 border-gray-300'
              ">
              <span class="flex gap-2 justify-center items-center text-sm capitalize">
                <p>{{ $t('common.next') }}</p>
                <IconChevronRight class="size-4" />
              </span>
            </button>
          </div>
        </div>

        <div
          class="flex justify-end items-start w-full"
          :class="isHorizontalLayout ? 'pr-8' : 'row-start-1'">
          <button
            v-if="editedLine.isModified && mode === 'edit'"
            class="p-1"
            @click="saveDocumentLine(editedLine)">
            <IconSave class="cursor-pointer size-4 hover:text-theme-600" />
          </button>
          <button
            class="p-1"
            @click="discardDocumentLine(editedLine)"
            v-if="editedLine.isModified && !isNewLine(editedLine) && mode === 'edit'">
            <IconXmark class="cursor-pointer size-4 hover:text-red-600" />
          </button>
          <div v-else class="flex justify-start items-center">
            <button
              v-if="!isNewLine(editedLine)"
              class="flex items-center justify-center p-[1px] rounded-full bg-slate-200 size-4"
              @click="
                onLineInfoClick(
                  editedLine.materialId,
                  editedLine.materialCode,
                  editedLine.materialName
                )
              ">
              <IconInfo class="rounded-full hover:text-theme-600 hove:bg-theme-100" />
            </button>
            <button
              @click="deleteLinePrompt(editedLine)"
              class="p-1"
              v-if="mode === 'edit' && (isNewLine(editedLine) || !editedLine.isModified)">
              <IconTrashOutline class="cursor-pointer size-4 hover:text-red-600" />
            </button>
          </div>
        </div>
      </div>
    </div>

    <MaterialAlterCodesSearchResultsModal
      v-if="alterCodesModalVisible"
      :search-results="materialAlterCodesList"
      @cancel="onMaterialAlterCodesSearchCancel"
      @select="onMaterialAlterCodeSelected" />

    <BaseModalConfirm
      v-if="showDeleteConfirmPrompt"
      @confirm="deleteLine"
      @cancel="showDeleteConfirmPrompt = false" />

    <SalesDocumentsDetailsFiltersModal
      v-if="isFiltersModalOpen"
      :active-filters="activeFilters"
      @cancel="closeFiltersModal"
      @save="onFiltersSelected"
      @reset-filters="removeAllFilters" />
  </section>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useLookupsStore } from '@/stores/lookups';
import { useSalesDocumentsStore } from '@/stores/salesDocuments';
import { useToastStore } from '@/stores/toast';
import { deepCopy } from '@/utils/data';
import { useI18n } from 'vue-i18n';

import BaseTableCellSearchbox from '@/components/common/BaseTableCellSearchbox.vue';
import BaseSelectDropdown from '@/components/common/BaseSelectDropdown.vue';
import BaseInput from '@/components/common/BaseInput.vue';
import MaterialAlterCodesSearchResultsModal from './MaterialAlterCodesSearchResultsModal.vue';
import BaseModalConfirm from '@/components/common/BaseModalConfirm.vue';
import IconAddSmall from '@/assets/icons/IconAddSmall.vue';
import IconSave from '@/assets/icons/IconSave.vue';
import IconTrashOutline from '@/assets/icons/IconTrashOutline.vue';
import IconXmark from '@/assets/icons/IconXmark.vue';
import IconChevronLeft from '@/assets/icons/IconChevronLeft.vue';
import IconChevronRight from '@/assets/icons/IconChevronRight.vue';
import IconInfo from '@/assets/icons/IconInfo.vue';
import IconFilters from '@/assets/icons/IconFilters.vue';
import BaseControlsButtonIcon from '@/components/common/BaseControlsButtonIcon.vue';
import SalesDocumentsDetailsFiltersModal from './SalesDocumentsDetailsFiltersModal.vue';
import type { DocumentDetail } from '@/types/Documents';
import { RecordMode, type SelectDropdownItem } from '@/types/Base';
import type { MaterialAlterCodeForSalesDocument } from '@/types/Materials';
import type { FilterGroupItems } from '@/types/Groups';
import useVuelidate from '@vuelidate/core';
import { useDebounceFn } from '@vueuse/core';
import BaseToggle from '@/components/common/BaseToggle.vue';

const props = defineProps<{ isHorizontalLayout?: boolean; mode: 'view' | 'edit' }>();

const toastStore = useToastStore();
const { t: $t } = useI18n();

const { modifiedSalesDocument, clearDetails, activeFilters } =
  storeToRefs(useSalesDocumentsStore());
const {
  initNewDocumentLine,
  searchMaterialAlterCodes,
  getMaterialDiscountsFromCustomerPricingPolicy,
  getMaterialPropertiesFromCustomerPricingPolicy,
  getMaterialVatFactor,
  updateDocLines,
  grossTotalChange,
  netTotalChange,
  priceOrQuantityChange,
  discountValueChange,
  discountPercentageChange,
  createLineRules,
  createLineValidationModel,
  saveDocumentLineInStore,
  updateExistsNotExistsFlags,
  processSiblingMaterialAlterCodes,
  initializeDynamicQuantities,
  getDynamicQuantityFields,
  setActiveFilters,
  removeFilter,
  clearAllFilters,
} = useSalesDocumentsStore();

const { dropdownWarehouses, warehouses, measurementUnits } = storeToRefs(useLookupsStore());
const { getWarehousesLookup, getMeasurementUnitsLookup } = useLookupsStore();

function isNewLine(item: DocumentDetail): boolean {
  if (!newLineAdded.value) return false;
  return newDocumentLine.value?.id === item.id;
}

const isUnitPriceDisabled = computed(() => {
  if (!editedLine.value) return true;
  const typeAllowManualPrices =
    !!modifiedSalesDocument.value?.documentHeader.documentType?.allowManualPrices;
  return (
    props.mode === 'view' ||
    !typeAllowManualPrices ||
    !editedLine.value.material ||
    !editedLine.value.material.allowManualPrice
  );
});

const alterCodesModalVisible = ref(false);
const newLineAdded = ref(false);
const showDeleteConfirmPrompt = ref(false);
const isFiltersModalOpen = ref(false);

const searchMaterialQuery = ref<string>('');

const editedLine = ref<DocumentDetail>();
const docDetailLines = ref<DocumentDetail[]>([]);
const newDocumentLine = ref<any>(null);
const materialAlterCodesList = ref<MaterialAlterCodeForSalesDocument[]>([]);
const displayLines = computed(() => {
  return docDetailLines.value.filter((l) => l.recordMode != RecordMode.Deleted);
});
onMounted(async () => {
  warehouses.value = [];
  getWarehousesLookup();
  getMeasurementUnitsLookup();

  docDetailLines.value = deepCopy<DocumentDetail[]>(
    modifiedSalesDocument.value?.documentHeader.documentDetails?.filter((d) => !d.deleted) ?? []
  );

  editedLine.value = docDetailLines.value[0];
});

const emit = defineEmits(['materialInfo']);

function onLineInfoClick(materialId: string, code: string, name: string) {
  emit('materialInfo', { materialId, code, name });
}

// Helper functions for quantity field management
function getQuantityValue(detail: DocumentDetail, unitIndex: number): number {
  switch (unitIndex) {
    case 1:
      return detail.quantity || 0;
    case 2:
      return detail.quantity2 || 0;
    case 3:
      return detail.quantity3 || 0;
    default:
      return 0;
  }
}

function setQuantityValue(detail: DocumentDetail, unitIndex: number, value: number) {
  switch (unitIndex) {
    case 1:
      detail.quantity = value;
      break;
    case 2:
      detail.quantity2 = value;
      break;
    case 3:
      detail.quantity3 = value;
      break;
  }
}

function isQuantityInputDisabled(detail: DocumentDetail, unitIndex: number): boolean {
  if (props.mode === 'view') {
    return true;
  }
  switch (unitIndex) {
    case 1:
      return (
        !detail.measurementUnit1Id ||
        detail.measurementUnit1Id === '00000000-0000-0000-0000-000000000000'
      );
    case 2:
      return (
        !detail.measurementUnit2Id ||
        detail.measurementUnit2Id === '00000000-0000-0000-0000-000000000000'
      );
    case 3:
      return (
        !detail.measurementUnit3Id ||
        detail.measurementUnit3Id === '00000000-0000-0000-0000-000000000000'
      );
    default:
      return true;
  }
}

watch(
  () => modifiedSalesDocument.value?.documentHeader.documentDetails,
  (newDetails) => {
    if (!newDetails) return;
    const newDetailsCopy = deepCopy<DocumentDetail[]>(newDetails);
    if (!clearDetails.value) {
      // when on edit mode I save one line but there are more that have been modified, do not overwrite them.
      const localUnsaved = docDetailLines.value.filter(
        (l) => l.isModified && l.id !== editedLine.value?.id
      );
      const localUnsavedIds = localUnsaved.map((x) => x.id);

      // Merge unsaved changes into the new details
      for (const newDetail of newDetailsCopy) {
        if (localUnsavedIds.includes(newDetail.id)) {
          const index = newDetailsCopy.findIndex((x) => x.id === newDetail.id);
          newDetailsCopy[index] = {
            ...newDetailsCopy[index],
            ...localUnsaved.find((x) => x.id === newDetail.id),
          };
        }
      }
    }
    docDetailLines.value = deepCopy<DocumentDetail[]>(
      newDetailsCopy.filter((d) => !d.deleted) || []
    );
    // Keep the new document line, if applicable
    if (newDocumentLine.value)
      docDetailLines.value = [newDocumentLine.value, ...docDetailLines.value];

    // Sync editedLine with updated data from store (important for unit transitions)
    if (editedLine.value) {
      const updatedLine = docDetailLines.value.find((line) => line.id === editedLine.value?.id);
      if (updatedLine) {
        editedLine.value = updatedLine;
      }
    }

    clearDetails.value = false;
  },
  { deep: false }
);

const previousButtonDisabled = ref(true);
const nextButtonDisabled = ref(false);
function selectPreviousLine() {
  const currentIndex = displayLines.value.findIndex((x) => x.id === editedLine.value?.id);

  if (currentIndex > 0) {
    const prevIndex = currentIndex - 1;
    editedLine.value = displayLines.value[prevIndex];

    previousButtonDisabled.value = prevIndex === 0;
    nextButtonDisabled.value = false;
  }
}

function selectNextLine() {
  const currentIndex = displayLines.value.findIndex((x) => x.id === editedLine.value?.id);

  if (currentIndex !== -1 && currentIndex < displayLines.value.length - 1) {
    const nextIndex = currentIndex + 1;
    editedLine.value = displayLines.value[nextIndex];

    nextButtonDisabled.value = nextIndex === displayLines.value.length - 1;
    previousButtonDisabled.value = false;
  }
}

async function onUpdateMaterialAlterCodesSearch(searchText: string, item: DocumentDetail) {
  editedLine.value = item;
  const results = await searchMaterialAlterCodes(
    searchText,
    undefined,
    activeFilters.value.map((f) => f.id)
  );
  if (results.length > 1) {
    materialAlterCodesList.value = results;
    alterCodesModalVisible.value = true;
  } else if (results.length === 1) {
    await onMaterialAlterCodeSelected(results[0]);
  } else {
    toastStore.attention({
      text: $t('messages.noResultsFor', { query: searchText }),
      hideTitle: true,
    });
    if (editedLine.value.materialAlterCode.alterCode) {
      editedLine.value.materialName = editedLine.value.materialAlterCode.alterCode.name;
      editedLine.value.materialCode = editedLine.value.materialAlterCode.alterCode.code;
    }
  }
  searchMaterialQuery.value = '';
}

async function onMaterialAlterCodeSelected(item: MaterialAlterCodeForSalesDocument) {
  if (!editedLine.value || !item) return;
  editedLine.value.materialAlterCode = item;
  editedLine.value.materialAlterCodeId = item.id ?? '';
  editedLine.value.materialCode = item.alterCode?.code ?? '';
  editedLine.value.materialName = item.alterCode?.name ?? '';
  editedLine.value.materialId = item.materialId ?? '';

  // Process sibling MaterialAlterCodes for measurement unit mapping
  if (item.allMaterialAlterCodes && item.allMaterialAlterCodes.length > 0) {
    editedLine.value = processSiblingMaterialAlterCodes(
      editedLine.value,
      item,
      Math.min(
        modifiedSalesDocument.value?.documentHeader?.documentType?.totalMeasurementUnits || 1,
        3
      )
    );
  } else {
    // Fallback to original logic if no sibling data
    editedLine.value.measurementUnit1Id = item.measurementUnitId ?? '';
  }

  editedLine.value = await getMaterialPropertiesFromCustomerPricingPolicy(editedLine.value);

  if (editedLine.value)
    editedLine.value = await getMaterialDiscountsFromCustomerPricingPolicy(editedLine.value);
  if (editedLine.value) editedLine.value = await getMaterialVatFactor(editedLine.value);

  if (editedLine.value)
    docDetailLines.value = updateDocLines(editedLine.value, docDetailLines.value);

  alterCodesModalVisible.value = false;
}

function onMaterialAlterCodesSearchCancel() {
  if (!editedLine.value) return;

  editedLine.value.materialName = editedLine.value.materialAlterCode?.alterCode?.name ?? '';
  editedLine.value.materialCode = editedLine.value.materialAlterCode?.alterCode?.code ?? '';

  alterCodesModalVisible.value = false;
}

function clearItem(line: DocumentDetail) {
  editedLine.value = line;
  if (editedLine.value) {
    editedLine.value.materialCode = '';
    editedLine.value.materialName = '';
    editedLine.value.materialAlterCodeId = '';
    docDetailLines.value = updateDocLines(editedLine.value, docDetailLines.value);
  }
}

function handleWarehouseSelection(event: SelectDropdownItem, item: DocumentDetail) {
  editedLine.value = item;
  if (editedLine.value) {
    const selectedWarehouse = warehouses.value.find((x) => x.id === event.value);
    if (selectedWarehouse) {
      editedLine.value.warehouseId = selectedWarehouse.id;
      editedLine.value.warehouse = selectedWarehouse;
    }
    docDetailLines.value = updateDocLines(editedLine.value, docDetailLines.value);
  }
}

function inputUpdated(
  header:
    | 'quantity'
    | 'unitPrice'
    | 'percentageDiscount'
    | 'unitDiscount'
    | 'grossTotal'
    | 'netTotal'
    | 'lineVatTotal'
    | 'exists'
    | 'notExists',
  item: DocumentDetail
) {
  editedLine.value = item;
  switch (header) {
    case 'quantity':
      editedLine.value = priceOrQuantityChange(editedLine.value, 'quantity');
      docDetailLines.value = updateDocLines(editedLine.value, docDetailLines.value);
      break;
    case 'unitPrice':
      editedLine.value = priceOrQuantityChange(editedLine.value, 'unitPrice');
      docDetailLines.value = updateDocLines(editedLine.value, docDetailLines.value);
      break;
    case 'unitDiscount':
      editedLine.value = discountValueChange(editedLine.value, validate(header, item.id));
      docDetailLines.value = updateDocLines(editedLine.value, docDetailLines.value);
      break;
    case 'percentageDiscount':
      editedLine.value = discountPercentageChange(editedLine.value, validate(header, item.id));
      docDetailLines.value = updateDocLines(editedLine.value, docDetailLines.value);
      break;
    case 'exists':
    case 'notExists':
      editedLine.value = updateExistsNotExistsFlags(
        editedLine.value,
        header as 'exists' | 'notExists'
      );
      docDetailLines.value = updateDocLines(editedLine.value, docDetailLines.value);
      break;
    case 'grossTotal':
      debounceGrossTotalChange();
      break;
    case 'netTotal':
      debounceNetTotalChange();
      break;
    default:
      break;
  }
}

const debounceGrossTotalChange = useDebounceFn(
  () => {
    if (editedLine.value) {
      editedLine.value = grossTotalChange(editedLine.value);
      docDetailLines.value = updateDocLines(editedLine.value, docDetailLines.value);
    }
  },
  700,
  { maxWait: 1200 }
);

const debounceNetTotalChange = useDebounceFn(
  () => {
    if (editedLine.value) {
      editedLine.value = netTotalChange(editedLine.value);
      docDetailLines.value = updateDocLines(editedLine.value, docDetailLines.value);
    }
  },
  700,
  { maxWait: 1200 }
);

function saveDocumentLine(line: DocumentDetail) {
  $v.value.$touch();
  if ($v.value.$invalid) return;
  editedLine.value = line;
  if (editedLine.value && editedLine.value.id == line.id) editedLine.value.isModified = false; // updates the memory object so as to control the toast notification
  saveDocumentLineInStore(line);

  if (newDocumentLine.value && newDocumentLine.value.id === line.id) {
    newDocumentLine.value = null;
    newLineAdded.value = false;
  }
}

function discardDocumentLine(line: DocumentDetail) {
  line.isModified = false;
  const index = docDetailLines.value.findIndex((x) => x.id === line.id);
  const lineBeforeChange = modifiedSalesDocument.value?.documentHeader.documentDetails?.find(
    (x) => x.id == line.id
  );
  if (index == undefined || index < 0 || !lineBeforeChange) return;
  docDetailLines.value[index] = {
    ...docDetailLines.value[index],
    ...lineBeforeChange,
  };
  editedLine.value = docDetailLines.value[index];
  editedLine.value.isModified = false;
  $v.value.$touch();
}

function deleteLinePrompt(item: DocumentDetail) {
  editedLine.value = item;
  showDeleteConfirmPrompt.value = true;
}

function deleteLine() {
  showDeleteConfirmPrompt.value = false;
  if (!editedLine.value) return;

  if (!!newLineAdded.value && newDocumentLine.value.id == editedLine.value.id) {
    const index = docDetailLines.value.findIndex((x) => x.id === newDocumentLine.value.id);
    if (index == -1) return;
    docDetailLines.value.splice(index, 1);
    newLineAdded.value = false;
    editedLine.value.recordMode = RecordMode.Deleted;
  } else {
    editedLine.value.recordMode = RecordMode.Deleted;
    saveDocumentLine(editedLine.value);
  }

  editedLine.value = docDetailLines.value[0] || undefined;
}

function addNewLine() {
  if (newLineAdded.value) return;
  newLineAdded.value = true;
  newDocumentLine.value = initNewDocumentLine();
  docDetailLines.value = [newDocumentLine.value, ...docDetailLines.value];

  const newLineIndex = displayLines.value.findIndex((x) => x.id == newDocumentLine.value.id);
  editedLine.value = displayLines.value[newLineIndex];
}

watch(
  () => editedLine.value,
  (newLine, oldLine) => {
    validationModel.value.materialAlterCodeId = newLine?.materialAlterCodeId || '';
    validationModel.value.totalQuantity = newLine?.totalQuantity || '';
    validationModel.value.unitPrice = newLine?.unitPrice || '';
    validationModel.value.grossTotal = newLine?.grossTotal || '';
    validationModel.value.discountValue = newLine?.unitDiscount || 0;
    validationModel.value.discountPercentage = newLine?.percentageDiscount || 0;
    validationModel.value.material = newLine?.material ?? null;

    // If switching to a new line while the previous line was modified, prompt the user to save changes
    if (newLine?.id != oldLine?.id && oldLine?.isModified)
      toastStore.attention({
        text: $t('messages.saveLineChangesPrompt'),
        hideTitle: true,
      });

    // If switching from the new document line, restore it as the edited line
    if (
      newDocumentLine.value &&
      oldLine &&
      newLine &&
      newLine.id != oldLine.id &&
      oldLine.id === newDocumentLine.value.id
    )
      newDocumentLine.value = oldLine;
  },
  { deep: true }
);

// Initialize measurement unit fields when component mounts
onMounted(() => {
  if (editedLine.value) {
    initializeDynamicQuantities(
      editedLine.value,
      modifiedSalesDocument.value?.documentHeader?.documentType
    );
  }
});

const rules = computed(() => createLineRules(editedLine.value ?? null));

const validationModel = ref(createLineValidationModel(editedLine.value ?? null));
const $v = useVuelidate(rules, validationModel, { $lazy: true });
function validate(header: string, id: string): string {
  if (!editedLine.value || id != editedLine.value.id) return '';
  switch (header) {
    case 'materialCode':
    case 'materialName':
      return $v.value.materialAlterCodeId?.$invalid
        ? $v.value.materialAlterCodeId.required.$message
        : '';
    case 'warehouse.name':
      return $v.value.warehouseId?.$invalid ? $v.value.warehouseId.required.$message : '';
    case 'unitPrice':
      return $v.value.unitPrice?.$invalid ? $v.value.unitPrice.docLineUnitPriceRule.$message : '';
    case 'grossTotal':
      return $v.value.grossTotal?.$invalid ? $v.value.grossTotal.required.$message : '';
    case 'totalQuantity':
      return $v.value.totalQuantity?.$invalid
        ? $v.value.totalQuantity.docLineQuantityRule.$message
        : '';
    case 'unitDiscount':
      if ($v.value.discountValue?.minValue.$invalid)
        return $v.value.discountValue.minValue.$message;
      else if ($v.value.discountValue?.maxValue.$invalid)
        return $v.value.discountValue?.maxValue.$message;
      else return '';
    case 'percentageDiscount':
      if ($v.value.discountPercentage?.minValue.$invalid)
        return $v.value.discountPercentage.minValue.$message;
      else if ($v.value.discountPercentage?.maxValue.$invalid)
        return $v.value.discountPercentage.maxValue.$message;
      else return '';
    default:
      return '';
  }
}

// Filter modal functions
function openFiltersModal() {
  isFiltersModalOpen.value = true;
}

function closeFiltersModal() {
  isFiltersModalOpen.value = false;
}

function onFiltersSelected(selectedFilters: FilterGroupItems[]) {
  setActiveFilters(selectedFilters);
  closeFiltersModal();
}

function handleSelection(item: FilterGroupItems) {
  removeFilter(item.id);
}

function removeAllFilters() {
  clearAllFilters();
  closeFiltersModal();
}

// Filter tags display logic
const visibleTagsNum = ref(20);
const maxVisibleTags = ref(20);
const showMore = ref(false);

const visibleTags = computed(() => {
  if (!activeFilters.value) return [];
  return activeFilters.value.slice(0, visibleTagsNum.value);
});

const remainingTags = computed(() => {
  if (!activeFilters.value || activeFilters.value.length <= visibleTagsNum.value) return 0;
  return activeFilters.value.length - visibleTagsNum.value;
});

function toggleVisibleTags() {
  if (showMore.value) {
    showMore.value = false;
    visibleTagsNum.value = 20;
  } else {
    showMore.value = true;
    visibleTagsNum.value = activeFilters.value?.length || 0;
  }
}
</script>
